import React, { useState, useRef, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { createPortal } from 'react-dom';
import { BudgetItem, getBudgetItems } from '../../api/budget';
import { getBudgetItemsByParentCode, getBudgetItemByCode } from '../../pages/Budget/BudgetTable/Templates/budgetTemplate';
import { generateBudgetItemId } from '../../utils/generateId';
import PinyinMatch from 'pinyin-match';
import DropdownMenu, { DropdownMenuItem } from '../DropdownMenu';
import { useBudgetDataCache } from '../../contexts/BudgetDataCacheContext';
import { useBudgetItemUpdates } from '../../hooks/dataUpdates/useBudgetItemUpdates';
import './RelatedBudget.css';
import {
  BudgetUnitInput,
  BUDGET_UNIT1_PRESETS,
  BUDGET_UNIT2_PRESETS,
} from '../BudgetUnitInput/BudgetUnitInput';
import { translateBudgetUnitPart } from '../../i18n/budgetUnitParts';

// 引入预算分类图标 (SVG)
import { ReactComponent as PreproductionIcon } from '../../assets/svg/Budget/Tpye/preproduction.svg';
import { ReactComponent as LeadsIcon } from '../../assets/svg/Budget/Tpye/leads.svg';
import { ReactComponent as CastIcon } from '../../assets/svg/Budget/Tpye/cast.svg';
import { ReactComponent as ExtrasIcon } from '../../assets/svg/Budget/Tpye/extras.svg';
import { ReactComponent as TeamIcon } from '../../assets/svg/Budget/Tpye/team.svg';
import { ReactComponent as ArtIcon } from '../../assets/svg/Budget/Tpye/art.svg';
import { ReactComponent as EquipmentIcon } from '../../assets/svg/Budget/Tpye/equipment.svg';
import { ReactComponent as TravelAccommodationIcon } from '../../assets/svg/Budget/Tpye/travel&accommodation.svg';
import { ReactComponent as LifeProductionIcon } from '../../assets/svg/Budget/Tpye/life&production.svg';
import { ReactComponent as PostproductionIcon } from '../../assets/svg/Budget/Tpye/postproduction.svg';
import { ReactComponent as InsuranceIcon } from '../../assets/svg/Budget/Tpye/insurance.svg';
import { ReactComponent as OthersIcon } from '../../assets/svg/Budget/Tpye/others.svg';
import uplevelIcon from '../../assets/images/Budget/uplevel.png';
import uplevelStopIcon from '../../assets/images/Budget/uplevel_stop.png';
import { ReactComponent as FatherIcon } from '../../assets/svg/Budget/father.svg';
import { ReactComponent as SonIcon } from '../../assets/svg/Budget/son.svg';

interface RelatedBudgetProps {
  targetBudgetItemID: string; // 触发弹窗的预算项ID
  budgetID: string; // 当前预算表ID
  visible: boolean;
  onClose: () => void;
  onRelated: (relatedItemID: string) => void; // 关联预算项回调
  onCreated: (newItem: BudgetItem) => void; // 创建新预算项回调
}

interface BudgetCategory {
  code: string;
  name: string;
  Icon: React.ComponentType<React.SVGProps<SVGSVGElement>>;
}

// 预算分类配置
const BUDGET_CATEGORIES: BudgetCategory[] = [
  { code: '01', name: '前期发展', Icon: PreproductionIcon },
  { code: '02', name: '主创', Icon: LeadsIcon },
  { code: '03', name: '其他演员', Icon: CastIcon },
  { code: '04', name: '特/临/替', Icon: ExtrasIcon },
  { code: '05', name: '团队/员工', Icon: TeamIcon },
  { code: '06', name: '美术', Icon: ArtIcon },
  { code: '07', name: '器材', Icon: EquipmentIcon },
  { code: '08', name: '交通住宿', Icon: TravelAccommodationIcon },
  { code: '09', name: '后勤支出', Icon: LifeProductionIcon },
  { code: '10', name: '后期', Icon: PostproductionIcon },
  { code: '11', name: '商保法', Icon: InsuranceIcon },
  { code: '12', name: '其他杂项', Icon: OthersIcon },
];

// 货币选项
const CURRENCY_OPTIONS = ['RMB', 'HK$', 'US$', 'EUR', 'JPY', 'GBP', 'CAD', 'AUD'] as const;

// 获取单位类型
const getUnitType = (u1: string | undefined, u2: string | undefined): 'none' | 'single' | 'compound' => {
  const unit1 = u1 && u1 !== '--' ? u1 : '';
  if (!unit1) return 'none';
  const unit2 = u2 && u2 !== '--' ? u2 : '';
  if (unit2) return 'compound';
  return 'single';
};

// 货币选择组件
const CurrencySelect: React.FC<{
  value: string;
  onChange: (value: string) => void;
  disabled?: boolean;
}> = ({ value, onChange, disabled }) => {
  const [menuVisible, setMenuVisible] = useState(false);
  const buttonRef = useRef<HTMLDivElement>(null);
  const [menuPosition, setMenuPosition] = useState({ top: 0, left: 0 });

  const currencyItems: DropdownMenuItem[] = CURRENCY_OPTIONS.map(currency => ({
    id: currency,
    label: currency,
  }));

  const calculatePosition = () => {
    if (buttonRef.current) {
      const rect = buttonRef.current.getBoundingClientRect();
      setMenuPosition({
        top: rect.bottom + 2,
        left: rect.left,
      });
    }
  };

  useEffect(() => {
    if (menuVisible) {
      // 使用 requestAnimationFrame 确保 DOM 已更新
      requestAnimationFrame(() => {
        calculatePosition();
      });

      // 监听滚动事件，更新菜单位置
      const handleScroll = () => {
        calculatePosition();
      };

      // 监听窗口滚动和表格容器滚动
      window.addEventListener('scroll', handleScroll, true);
      const tableContainer = document.querySelector('.budget-table-container');
      if (tableContainer) {
        tableContainer.addEventListener('scroll', handleScroll, true);
      }

      return () => {
        window.removeEventListener('scroll', handleScroll, true);
        if (tableContainer) {
          tableContainer.removeEventListener('scroll', handleScroll, true);
        }
      };
    }
  }, [menuVisible]);

  const handleClick = () => {
    if (!disabled) {
      // 在打开菜单前立即计算位置
      calculatePosition();
      setMenuVisible(!menuVisible);
    }
  };

  const handleSelect = (item: DropdownMenuItem) => {
    onChange(item.id as string);
    setMenuVisible(false);
  };

  const menu = menuVisible && !disabled ? (
    <DropdownMenu
      items={currencyItems}
      visible={true}
      onClose={() => setMenuVisible(false)}
      onSelect={handleSelect}
      position={menuPosition}
      useFixedPosition={true}
      selectedValue={value}
      width={buttonRef.current?.offsetWidth}
    />
  ) : null;

  return (
    <>
      <div
        ref={buttonRef}
        className={`progress-type-select custom-dropdown ${disabled ? 'disabled' : ''}`}
        onClick={handleClick}
        style={{
          cursor: disabled ? 'not-allowed' : 'pointer',
          opacity: disabled ? 0.5 : 1,
          userSelect: 'none',
          width: '100%',
          height: '32px',
          padding: '0 12px',
          paddingRight: '32px',
          border: '1px solid #e5e5e5',
          borderRadius: '6px',
          display: 'flex',
          alignItems: 'center',
          fontSize: '12px',
          background: 'white',
        }}
      >
        {value || 'RMB'}
      </div>
      {menu && createPortal(menu, document.body)}
    </>
  );
};

// 将树形结构扁平化为数组
const flattenBudgetItems = (items: any[]): BudgetItem[] => {
  const result: BudgetItem[] = [];
  const traverse = (items: any[]) => {
    items.forEach(item => {
      // 排除虚拟相关预算项
      if (!item.isRelatedItem) {
        result.push({
          ...item,
          budgetItemChildren: undefined, // 移除 children，因为我们需要扁平数组
        });
      }
      if (item.budgetItemChildren && item.budgetItemChildren.length > 0) {
        traverse(item.budgetItemChildren);
      }
    });
  };
  traverse(items);
  return result;
};

const RelatedBudget: React.FC<RelatedBudgetProps> = ({
  targetBudgetItemID,
  budgetID,
  visible,
  onClose,
  onRelated,
  onCreated
}) => {
  const { t } = useTranslation();
  const { getCache, subscribeBudgetCache } = useBudgetDataCache();
  const { createBudgetItemData, updateBudgetItemData } = useBudgetItemUpdates();
  
  // 标签页状态
  const [activeTab, setActiveTab] = useState<'create' | 'relate'>('create');
  
  // 搜索/浏览模式状态
  const [mode, setMode] = useState<'search' | 'create'>('search');
  const [searchText, setSearchText] = useState('');
  const [searchResults, setSearchResults] = useState<BudgetItem[]>([]);
  const [selectedItems, setSelectedItems] = useState<Set<string>>(new Set());
  const [allBudgetItems, setAllBudgetItems] = useState<BudgetItem[]>([]);
  const [loading, setLoading] = useState(false);
  
  // 创建模式相关状态
  const [currentPath, setCurrentPath] = useState<string[]>([]); // 当前路径 ['08', '0802']
  const [currentItems, setCurrentItems] = useState<any[]>([]); // 当前层级的项目
  const [selectedCreateItem, setSelectedCreateItem] = useState<string | null>(null);
  const [selectedCreateItemID, setSelectedCreateItemID] = useState<string | null>(null); // 选择的父级预算项ID
  const [popupUpward, setPopupUpward] = useState(false);
  
  // 创建表单数据
  const [createFormData, setCreateFormData] = useState({
    budgetItemName: '',
    budgetItemCurrency: 'RMB',
    budgetItemUnit1: '--',
    budgetItemUnit2: '--',
    budgetItemUnitPrice: 0,
    budgetItemQuantity1: 1,
    budgetItemQuantity2: 1,
  });
  
  const searchRef = useRef<HTMLDivElement>(null);

  // 检测弹出方向
  useEffect(() => {
    if (!visible) return;
    
    // 通过父容器位置检测是否需要向上弹出
    const container = searchRef.current?.parentElement;
    if (container) {
      const rect = container.getBoundingClientRect();
      const windowHeight = window.innerHeight;
      const shouldPopupUpward = rect.bottom > windowHeight * 0.6; // 如果按钮在视窗下40%部分，向上弹出
      setPopupUpward(shouldPopupUpward);
    }
  }, [visible]);

  // 从缓存加载预算项数据
  const loadBudgetItemsFromCache = useCallback(() => {
    if (!budgetID) return;
    
    const cache = getCache(budgetID);
    if (cache && cache.budgetItems) {
      // 将树形结构扁平化为数组
      const flatItems = flattenBudgetItems(cache.budgetItems);
      setAllBudgetItems(flatItems);
      setLoading(false);
    } else {
      // 如果缓存不存在，从服务器加载
      setLoading(true);
      getBudgetItems(budgetID)
        .then(response => {
          if (response.data) {
            setAllBudgetItems(response.data);
          }
        })
        .catch(error => {
          console.error('获取预算项失败:', error);
        })
        .finally(() => {
          setLoading(false);
        });
    }
  }, [budgetID, getCache]);

  // 获取所有预算项数据（从缓存或服务器）
  useEffect(() => {
    if (!visible) return;
    loadBudgetItemsFromCache();
  }, [visible, budgetID, loadBudgetItemsFromCache]);

  // 订阅缓存变化
  useEffect(() => {
    if (!budgetID || !visible) return;
    
    const unsubscribe = subscribeBudgetCache(budgetID, () => {
      // 缓存更新时，重新加载数据
      loadBudgetItemsFromCache();
    });

    return unsubscribe;
  }, [budgetID, visible, subscribeBudgetCache, loadBudgetItemsFromCache]);

  // 处理搜索
  const handleSearch = (text: string) => {
    setSearchText(text);
    if (!text.trim()) {
      setSearchResults([]);
      setMode('search');
      return;
    }

    const filteredResults = allBudgetItems.filter(item => {
      // 排除目标预算项本身
      if (item.budgetItemID === targetBudgetItemID) return false;
      
      const matchCode = item.budgetItemCode && 
        item.budgetItemCode.toLowerCase().includes(text.toLowerCase());
      
      const matchName = item.budgetItemName && (
        item.budgetItemName.toLowerCase().includes(text.toLowerCase()) ||
        PinyinMatch.match(item.budgetItemName, text)
      );

      return matchCode || matchName;
    });

    setSearchResults(filteredResults);
    setMode('search');
  };

  // 处理项目选择
  const handleItemSelect = (itemID: string, checked: boolean) => {
    const newSelected = new Set(selectedItems);
    if (checked) {
      newSelected.clear(); // 单选模式，清除其他选择
      newSelected.add(itemID);
      
      // 如果是创建模式，选择后直接设置为父级并显示表单
      if (activeTab === 'create') {
        const selectedItem = allBudgetItems.find(item => item.budgetItemID === itemID);
        if (selectedItem) {
          setSelectedCreateItem(selectedItem.budgetItemCode);
          setSelectedCreateItemID(selectedItem.budgetItemID);
          setSearchText('');
          setSearchResults([]);
          // 保留选中状态用于显示
        }
      }
    } else {
      newSelected.delete(itemID);
      if (activeTab === 'create') {
        setSelectedCreateItem(null);
        setSelectedCreateItemID(null);
      }
    }
    setSelectedItems(newSelected);
  };

  // 处理关联操作
  const handleRelate = async () => {
    if (selectedItems.size === 0) return;
    
    try {
      // 这里只处理单选，取第一个选中项
      const selectedItemID = Array.from(selectedItems)[0];
      
      // 使用统一更新 hook 更新目标预算项的 budgetItemRelatedID
      await updateBudgetItemData(budgetID, targetBudgetItemID, {
        budgetItemRelatedID: selectedItemID
      });
      
      onRelated(selectedItemID);
      onClose();
      resetState();
    } catch (error) {
      console.error('关联预算项失败:', error);
    }
  };

  // 重置状态
  const resetState = () => {
    setMode('search');
    setSearchText('');
    setSearchResults([]);
    setSelectedItems(new Set());
    setCurrentPath([]);
    setCurrentItems([]);
    setSelectedCreateItem(null);
    setSelectedCreateItemID(null);
    setPopupUpward(false);
    setCreateFormData({
      budgetItemName: '',
      budgetItemCurrency: 'RMB',
      budgetItemUnit1: '--',
      budgetItemUnit2: '--',
      budgetItemUnitPrice: 0,
      budgetItemQuantity1: 1,
      budgetItemQuantity2: 1,
    });
  };

  // 处理分类图标点击
  const handleCategoryClick = (categoryCode: string) => {
    if (activeTab === 'create') {
      setMode('create');
      setCurrentPath([categoryCode]);
      loadCurrentLevelItems([categoryCode]);
    } else {
      setMode('create');
      setCurrentPath([categoryCode]);
      loadCurrentLevelItems([categoryCode]);
    }
  };

  // 加载当前层级的项目
  const loadCurrentLevelItems = (path: string[]) => {
    const currentCode = path[path.length - 1];
    const childItems = getBudgetItemsByParentCode(currentCode);
    setCurrentItems(childItems);
  };

  // 检查项目是否有子级
  const hasChildItems = (itemCode: string): boolean => {
    const childItems = getBudgetItemsByParentCode(itemCode);
    return childItems.length > 0;
  };

  // 处理上一级按钮
  const handleUpLevel = () => {
    if (currentPath.length <= 1) return;
    
    const newPath = currentPath.slice(0, -1);
    setCurrentPath(newPath);
    loadCurrentLevelItems(newPath);
    setSelectedCreateItem(null);
  };

  // 处理项目双击（进入下一级）
  const handleItemDoubleClick = (itemCode: string) => {
    const newPath = [...currentPath, itemCode];
    setCurrentPath(newPath);
    loadCurrentLevelItems(newPath);
    setSelectedCreateItem(null);
    setSelectedCreateItemID(null);
  };

  // 处理创建模式下的项目选择
  const handleCreateItemSelect = (itemCode: string, itemID: string) => {
    setSelectedCreateItem(itemCode);
    setSelectedCreateItemID(itemID);
    // 重置表单数据，但保留货币（可以从父级继承）
    const parentItem = allBudgetItems.find(item => item.budgetItemID === itemID);
    setCreateFormData(prev => ({
      ...prev,
      budgetItemCurrency: parentItem?.budgetItemCurrency || 'RMB',
    }));
  };

  // 生成新的预算项代码
  const generateNewItemCode = (parentCode: string) => {
    // 获取父级的所有子项
    const existingItems = allBudgetItems.filter(item => 
      item.budgetItemParentID && 
      item.budgetItemCode?.startsWith(parentCode)
    );
    
    // 找出最大的编号
    let maxNum = 0;
    const codeLength = parentCode.length + 2; // 父级代码长度 + 2位数字
    
    existingItems.forEach(item => {
      if (item.budgetItemCode && item.budgetItemCode.length === codeLength) {
        const suffix = item.budgetItemCode.substring(parentCode.length);
        const num = parseInt(suffix, 10);
        if (!isNaN(num)) {
          maxNum = Math.max(maxNum, num);
        }
      }
    });
    
    // 生成新编号
    const newNum = maxNum + 1;
    const newSuffix = newNum.toString().padStart(2, '0');
    return parentCode + newSuffix;
  };

  // 处理创建预算项
  const handleCreate = async () => {
    if (!selectedCreateItemID || !createFormData.budgetItemName.trim()) return;
    
    try {
      // 找到父级预算项
      const parentBudgetItem = allBudgetItems.find(item => 
        item.budgetItemID === selectedCreateItemID
      );
      
      if (!parentBudgetItem) return;
      
      // 生成新的预算项代码
      const newCode = generateNewItemCode(parentBudgetItem.budgetItemCode);
      
      // 生成新的预算项ID
      const newItemID = generateBudgetItemId();
      
      // 计算金额
      const amount = (createFormData.budgetItemUnitPrice || 0) * 
                     (createFormData.budgetItemQuantity1 || 1) * 
                     (createFormData.budgetItemQuantity2 || 1);
      
      // 创建新预算项（使用后端要求的中文枚举值）
      const newItemData: Partial<BudgetItem> = {
        budgetItemID: newItemID,
        budgetItemCode: newCode,
        budgetItemName: createFormData.budgetItemName.trim(),
        budgetItemBelongBudgetID: budgetID,
        budgetItemParentID: selectedCreateItemID, // 父级预算项ID
        budgetItemLevel: (parentBudgetItem.budgetItemLevel || 0) + 1,
        budgetItemRelatedID: targetBudgetItemID, // 关联到触发的预算项
        budgetItemRole: '可申请可拨发', // 使用中文枚举值
        budgetItemProgressType: '按时间进度', // 使用中文枚举值
        budgetItemCurrency: createFormData.budgetItemCurrency,
        budgetItemUnit1: createFormData.budgetItemUnit1 === '--' ? undefined : createFormData.budgetItemUnit1,
        budgetItemUnit2: createFormData.budgetItemUnit2 === '--' ? undefined : createFormData.budgetItemUnit2,
        budgetItemUnitPrice: createFormData.budgetItemUnitPrice,
        budgetItemQuantity1: createFormData.budgetItemQuantity1,
        budgetItemQuantity2: createFormData.budgetItemQuantity2,
        budgetItemAmount: amount,
        budgetItemOwnerID: parentBudgetItem.budgetItemOwnerID || undefined,
        budgetItemAssigneeID: parentBudgetItem.budgetItemAssigneeID || undefined,
        budgetItemCreatorID: parentBudgetItem.budgetItemCreatorID || undefined,
        budgetItemRemark: '',
        budgetItemUsedAmount: 0,
        budgetItemUnusedAmount: 0,
        budgetItemExceedingAmount: 0,
        budgetItemDataStatus: '正常', // 使用中文枚举值
        budgetItemStatus: '预算中' // 使用中文枚举值
      };
      
      // 使用统一更新 hook 创建预算项
      const createdItem = await createBudgetItemData(budgetID, newItemData);
      
      if (createdItem) {
        onCreated(createdItem);
        onClose();
        resetState();
      }
    } catch (error) {
      console.error('创建预算项失败:', error);
    }
  };

  // 处理点击外部关闭
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (searchRef.current && !searchRef.current.contains(event.target as Node)) {
        onClose();
        resetState();
      }
    };

    if (visible) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [visible, onClose]);

  if (!visible) return null;

  // 获取地址栏显示文本
  const getAddressBarText = () => {
    return currentPath.map(code => {
      const item = getBudgetItemByCode(code);
      return `${code} ${item?.budgetItemName || ''}`;
    }).join(' / ');
  };

  return (
    <div className="related-budget" ref={searchRef}>
      <div className={`related-budget-dialog ${popupUpward ? 'popup-upward' : ''}`}>
        {/* 标签切换 */}
        <div className="related-budget-tabs">
          <button
            className={`related-budget-tab ${activeTab === 'create' ? 'active' : ''}`}
            onClick={() => {
              setActiveTab('create');
              resetState();
            }}
          >
            创建一个关联的预算项
          </button>
          <button
            className={`related-budget-tab ${activeTab === 'relate' ? 'active' : ''}`}
            onClick={() => {
              setActiveTab('relate');
              resetState();
            }}
          >
            关联到其他预算项
          </button>
        </div>

        <div className="related-budget-input">
          {activeTab === 'create' && selectedCreateItemID ? (
            <>
              <div className="search-title">已选择父级预算项</div>
              <div className="create-address-input" style={{ background: '#f0f7ff', borderColor: '#667DE9' }}>
                <div className="address-path">
                  {(() => {
                    const selectedItem = allBudgetItems.find(item => item.budgetItemID === selectedCreateItemID);
                    return selectedItem ? `${selectedItem.budgetItemCode} ${selectedItem.budgetItemName}` : '';
                  })()}
                </div>
                <button
                  className="up-level-btn"
                  onClick={() => {
                    setSelectedCreateItem(null);
                    setSelectedCreateItemID(null);
                    setSearchText('');
                    setSearchResults([]);
                    setSelectedItems(new Set());
                  }}
                  style={{ marginLeft: '8px' }}
                >
                  ✕
                </button>
              </div>
            </>
          ) : mode === 'search' ? (
            <>
              <div className="search-title">
                {activeTab === 'create' ? '搜索你要创建的预算项的父级预算' : '搜索你要关联的预算项'}
              </div>
              <input
                type="text"
                value={searchText}
                onChange={(e) => handleSearch(e.target.value)}
                placeholder="搜索预算代码、名称..."
                autoFocus
              />
            </>
          ) : (
            <>
              <div className="search-title">搜索你要创建的预算项的父级预算</div>
              <div className="create-address-input">
                <button 
                  className="up-level-btn"
                  onClick={handleUpLevel}
                  disabled={currentPath.length <= 1}
                >
                  <img 
                    src={currentPath.length <= 1 ? uplevelStopIcon : uplevelIcon} 
                    alt="上一级" 
                  />
                </button>
                <div className="address-path">{getAddressBarText()}</div>
              </div>
            </>
          )}
        </div>
        
        <div className="related-budget-content">
          {loading ? (
            <div className="related-budget-loading">加载中...</div>
          ) : activeTab === 'create' && selectedCreateItemID ? (
            // 创建模式 - 显示表单
            <>
              {/* 创建表单 */}
              <div className="create-form">
                {/* 第一行：预算项名称、货币、单位 */}
                <div className="create-form-row">
                  <div className="create-form-field create-form-field-wide">
                    <label>预算项名称</label>
                    <input
                      type="text"
                      value={createFormData.budgetItemName}
                      onChange={(e) => setCreateFormData(prev => ({ ...prev, budgetItemName: e.target.value }))}
                      placeholder="请输入预算项名称"
                    />
                  </div>
                  <div className="create-form-field">
                    <label>货币</label>
                    <CurrencySelect
                      value={createFormData.budgetItemCurrency}
                      onChange={(value) => setCreateFormData(prev => ({ ...prev, budgetItemCurrency: value }))}
                    />
                  </div>
                  <div className="create-form-field">
                    <label>单位¹</label>
                    <div style={{ width: '100%' }}>
                      <BudgetUnitInput
                        variant="form"
                        dropdownId="related-budget-create-unit1"
                        value={createFormData.budgetItemUnit1}
                        presets={BUDGET_UNIT1_PRESETS}
                        translateUnitLabel={(u) => translateBudgetUnitPart(u, t)}
                        onChange={(value) => setCreateFormData(prev => ({
                          ...prev,
                          budgetItemUnit1: value,
                          ...(value === '--' ? {
                            budgetItemUnit2: '--' as const,
                            budgetItemQuantity1: 1,
                            budgetItemQuantity2: 1,
                          } : {}),
                        }))}
                      />
                    </div>
                  </div>
                  <div className="create-form-field">
                    <label>单位²</label>
                    <div style={{ width: '100%' }}>
                      <BudgetUnitInput
                        variant="form"
                        dropdownId="related-budget-create-unit2"
                        value={createFormData.budgetItemUnit2}
                        presets={BUDGET_UNIT2_PRESETS}
                        translateUnitLabel={(u) => translateBudgetUnitPart(u, t)}
                        onChange={(value) => setCreateFormData(prev => ({
                          ...prev,
                          budgetItemUnit2: value,
                          ...(value === '--' ? { budgetItemQuantity2: 1 } : {}),
                        }))}
                      />
                    </div>
                  </div>
                </div>
                {/* 第二行：根据单位类型显示不同字段 */}
                {getUnitType(createFormData.budgetItemUnit1, createFormData.budgetItemUnit2) !== 'none' && (
                  <div className="create-form-row">
                    <div className="create-form-field create-form-field-wide">
                      <label>单价</label>
                      <input
                        type="number"
                        value={createFormData.budgetItemUnitPrice || ''}
                        onChange={(e) => setCreateFormData(prev => ({ ...prev, budgetItemUnitPrice: parseFloat(e.target.value) || 0 }))}
                        placeholder="0"
                      />
                    </div>
                    <div className="create-form-field">
                      <label>数量¹</label>
                      <input
                        type="number"
                        value={createFormData.budgetItemQuantity1 || ''}
                        onChange={(e) => setCreateFormData(prev => ({ ...prev, budgetItemQuantity1: parseFloat(e.target.value) || 1 }))}
                        placeholder="1"
                      />
                    </div>
                    {getUnitType(createFormData.budgetItemUnit1, createFormData.budgetItemUnit2) === 'compound' && (
                      <div className="create-form-field">
                        <label>数量²</label>
                        <input
                          type="number"
                          value={createFormData.budgetItemQuantity2 || ''}
                          onChange={(e) => setCreateFormData(prev => ({ ...prev, budgetItemQuantity2: parseFloat(e.target.value) || 1 }))}
                          placeholder="1"
                        />
                      </div>
                    )}
                  </div>
                )}
              </div>
              
              <div className="search-results-action">
                <button 
                  className="action-btn" 
                  onClick={handleCreate}
                  disabled={!createFormData.budgetItemName.trim()}
                >
                  创建
                </button>
              </div>
            </>
          ) : mode === 'search' ? (
            // 搜索模式
            <>
              {!searchText ? (
                // 显示12个分类图标
                <>
                  <div className="budget-categories">
                    {BUDGET_CATEGORIES.map(category => (
                      <div 
                        key={category.code}
                        className="budget-category"
                        onClick={() => {
                          if (activeTab === 'create') {
                            handleCategoryClick(category.code);
                          } else {
                            // 关联模式下，点击分类也进入创建浏览模式
                            setMode('create');
                            setCurrentPath([category.code]);
                            loadCurrentLevelItems([category.code]);
                          }
                        }}
                      >
                        <category.Icon className="budget-category-icon" aria-label={category.name} />
                        <span>{category.name}</span>
                      </div>
                    ))}
                  </div>
                </>
              ) : (
                // 显示搜索结果
                <>
                  <div className="search-results">
                    {searchResults.length > 0 ? (
                      searchResults.map(item => (
                        <div 
                          key={item.budgetItemID} 
                          className={`search-result-item ${selectedItems.has(item.budgetItemID) ? 'selected' : ''}`}
                          onClick={() => handleItemSelect(item.budgetItemID, !selectedItems.has(item.budgetItemID))}
                        >
                          <span className="item-code">{item.budgetItemCode}</span>
                          <span className="item-name">{item.budgetItemName}</span>
                        </div>
                      ))
                    ) : (
                      <div className="no-results">未找到匹配的预算项</div>
                    )}
                  </div>
                  
                  {activeTab === 'relate' && (
                    <div className="search-results-action">
                      <button 
                        className="action-btn" 
                        onClick={handleRelate}
                        disabled={selectedItems.size === 0}
                      >
                        关联
                      </button>
                    </div>
                  )}
                </>
              )}

            </>
          ) : (
            // 创建浏览模式（分类逐级选择）
            <>
              <div className="search-results">
                {currentItems.length > 0 ? (
                  currentItems.map(item => {
                    // 找到对应的预算项ID
                    const budgetItem = allBudgetItems.find(bi => bi.budgetItemCode === item.budgetItemCode);
                    const isSelected = activeTab === 'create' 
                      ? selectedCreateItem === item.budgetItemCode
                      : selectedItems.has(budgetItem?.budgetItemID || '');
                    const hasChildren = hasChildItems(item.budgetItemCode);
                    
                    return (
                      <div 
                        key={item.budgetItemCode}
                        className={`search-result-item ${isSelected ? 'selected' : ''}`}
                        onClick={() => {
                          // 单击进入下一级（如果有子级）
                          if (hasChildren) {
                            handleItemDoubleClick(item.budgetItemCode);
                          }
                        }}
                      >
                        {hasChildren ? (
                          <FatherIcon className="item-icon" />
                        ) : (
                          <SonIcon className="item-icon" />
                        )}
                        <span className="item-code">{item.budgetItemCode}</span>
                        <span className="item-name">{item.budgetItemName}</span>
                        <button
                          className="select-item-btn"
                          onClick={(e) => {
                            e.stopPropagation();
                            if (activeTab === 'create' && budgetItem) {
                              handleCreateItemSelect(item.budgetItemCode, budgetItem.budgetItemID);
                            } else if (activeTab === 'relate' && budgetItem) {
                              handleItemSelect(budgetItem.budgetItemID, !selectedItems.has(budgetItem.budgetItemID));
                            }
                          }}
                        >
                          选择
                        </button>
                      </div>
                    );
                  })
                ) : (
                  <div className="no-results">该层级暂无子项目</div>
                )}
              </div>
              
              {activeTab === 'relate' && (
                <div className="search-results-action">
                  <button 
                    className="action-btn" 
                    onClick={handleRelate}
                    disabled={selectedItems.size === 0}
                  >
                    关联
                  </button>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default RelatedBudget;